/**
 * Fetch approval list filter options from the API (same pattern as change requests /
 * assets filters). Passing selected filters scopes other facet options.
 */
import { useMemo } from "react";
import useSWR from "swr";
import { endpoints } from "../../../../../endpoints";
import type {
  ApprovalsFiltersResponse,
  ApprovalsQueryParams,
} from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { buildApprovalsSearchParams } from "./buildApprovalsSearchParams";
import { useKeepLastValue } from "./useKeepLastValue";

export function useApprovalsFilters({
  productId,
  status,
  assignee_ids,
  approver_ids,
  stage,
  requested_by_ids,
  approval_type,
  item_details,
}: ApprovalsQueryParams & { productId?: string } = {}) {
  const { storefront_id } = useStoreState();

  const params = useMemo(
    () => ({
      params: buildApprovalsSearchParams({
        product_id_or_slug: productId,
        status,
        assignee_ids,
        approver_ids,
        stage,
        requested_by_ids,
        approval_type,
        item_details,
      }),
    }),
    [
      productId,
      status,
      assignee_ids,
      approver_ids,
      stage,
      requested_by_ids,
      approval_type,
      item_details,
    ]
  );

  const { data, error } = useSWR<ApprovalsFiltersResponse>(
    storefront_id
      ? [
          endpoints.v2_storefronts_id_integrations_approvals_filters(
            storefront_id
          ),
          params,
        ]
      : null
  );

  const filters = useKeepLastValue(data);

  return {
    filters,
    error,
    isLoading: !data && !filters && !error,
  };
}
